import {
  Box,
  Button,
  Flex,
  Text,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  TabPanel,
  TabPanels,
  Tabs,
  SimpleGrid,
  Link as ChakraLink,
  Heading,
} from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams, useLocation, useNavigationType } from "react-router-dom";
import { HistoryApplicationBlock } from "@/components/ApplicationBlocks/HistoryApplicationBlock";
import { Layout } from "@/components/Layout/Layout";
import { useAppDispatch, useAppSelector } from "@/store";
import { clearSingleApplication, fetchSingleApplication } from "@/store/slices/applications";
import { useEffect, useState } from "react";
import PageLoaderWrapper from "@/components/PageLoaderWrapper";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { AttachmentIcon } from "@chakra-ui/icons";
import { FiEdit, FiClock, FiUserPlus, FiShoppingBag } from "react-icons/fi";
import { ChakraTabs } from "@/components/tabs/ChakraTabs";
import { formatDate } from "@/helpers/formatDate";
import { AssignModal } from "@/components/AssignModal/AssignModal";
import api from "@/api";
import { downloadFile } from "@/helpers/DownloadFile";

export const SingleApplicationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [isShowHistory, setShowHistory] = useState(false);
  const [isAssignOpen, setAssignOpen] = useState(false);
  const [channels, setChannels] = useState<any[]>([]);

  const { currentApplication, isLoading } = useAppSelector((state) => state.applications);

  useEffect(() => {
    if (id !== undefined) {
      dispatch(fetchSingleApplication({ params: { id: Number(id) } }));
    }
    return () => {
      dispatch(clearSingleApplication());
    };
  }, [id, dispatch]);

  const [searchParams] = useSearchParams();

  const getActiveTabIndex = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = ["details", "vacancies"].indexOf(tabParam);
      if (tabIndex !== -1) {
        return tabIndex;
      }
    }
    return 0;
  };

  const activeTabIndex = getActiveTabIndex();

  useEffect(() => {
    if (!isAssignOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.channel.getChannels();
        if (!cancelled) setChannels(data || []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setChannels([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAssignOpen]);

  const handleBackToList = () => {
    const cameFromList = location.state?.fromList === true || searchParams.get("from") === "list";

    if (cameFromList && navType !== "POP") {
      navigate(-1);
      return;
    }

    navigate("/applications");
  };

  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных заявки...">
        <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
          <Text color="gray.500" cursor="pointer" onClick={handleBackToList} display="flex" alignItems="center" gap="8px">
            <ArrowBackIcon />К списку заявок
          </Text>
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize={24} fontWeight={500}>
              {currentApplication?.name}
            </Text>
            <Flex gap="16px">
              <Button
                leftIcon={<Icon as={FiUserPlus as any} boxSize={4} />}
                onClick={() => setAssignOpen(true)}
                isDisabled={!currentApplication?.can_manage_channel}
              >
                Назначить
              </Button>
              <Button leftIcon={<Icon as={FiEdit as any} boxSize={4} />} onClick={() => navigate("edit")} isDisabled={!currentApplication?.can_edit}>
                Редактировать
              </Button>
              <Button leftIcon={<Icon as={FiClock as any} boxSize={4} />} variant="outline" onClick={() => setShowHistory(true)}>
                История
              </Button>
            </Flex>
          </Flex>
          <HistoryModal isOpen={isShowHistory} onClose={() => setShowHistory(false)} />
          <AssignModal
            isOpen={isAssignOpen}
            onClose={() => setAssignOpen(false)}
            onConfirm={() => {
              if (!id) return;
              dispatch(fetchSingleApplication({ params: { id: Number(id) } }));
            }}
            applicationId={Number(id)}
            applicationName={currentApplication?.name}
            customerName={currentApplication?.customer?.name}
            channels={channels}
            assigned={currentApplication?.channels || []}
          />
          <Flex flexDirection="column" gap="32px">
            <Flex flexDirection="column" gap="16px" flex={1} minW={0}>
              <Tabs index={activeTabIndex} isLazy>
                <ChakraTabs
                  tabs={[
                    { id: "details", label: "Детали" },
                    { id: "vacancies", label: "Связанные вакансии" },
                  ]}
                  baseUrl={`/applications/${id}`}
                />
                <TabPanels paddingTop="16px">
                  <TabPanel paddingX={0} paddingTop={0}>
                    <Flex gap="16px" alignItems="flex-start">
                      <Flex flexDirection="column" gap="16px" flex={1} minW={0}>
                        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                          <Flex alignItems="center" gap="8px" mb="16px">
                            <Heading as="h3" size="md" fontWeight={600}>
                              Информация о проекте
                            </Heading>
                          </Flex>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <InfoItem label="Проект">{currentApplication?.project?.name ?? "—"}</InfoItem>
                            <InfoItem label="Заказчик">{currentApplication?.customer?.name ?? "—"}</InfoItem>
                            <InfoItem label="Юр. лицо">{currentApplication?.legal_entity?.name ?? "—"}</InfoItem>
                            <InfoItem label="Ссылка на договор (Bitrix 24)">
                              {(() => {
                                const link =
                                  (currentApplication as any)?.bitrix_contract_link || (currentApplication as any)?.project?.bitrix_contract_link;
                                return link ? (
                                  <ChakraLink href={link as any} isExternal color="#111827">
                                    Открыть договор
                                  </ChakraLink>
                                ) : (
                                  "—"
                                );
                              })()}
                            </InfoItem>
                          </SimpleGrid>
                          <Flex direction="column" gap="6px" mt="16px">
                            <Text fontSize="14px" color="#374151" fontWeight={700}>
                              Описание проекта
                            </Text>
                            <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
                              {currentApplication?.project_description || "—"}
                            </Text>
                          </Flex>
                        </Box>

                        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                          <Heading as="h3" size="md" fontWeight={600} mb="12px">
                            Требования к кандидату
                          </Heading>
                          <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
                            {currentApplication?.candidate_requirements || "—"}
                          </Text>
                        </Box>

                        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                          <Heading as="h3" size="md" fontWeight={600} mb="12px">
                            Обязанности кандидата
                          </Heading>
                          <Text color="#4a5568" fontSize="14px" lineHeight="1.5" whiteSpace="pre-line">
                            {currentApplication?.job_duties || "—"}
                          </Text>
                        </Box>

                        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                          <Flex alignItems="center" gap="8px" mb="16px">
                            <Heading as="h3" size="md" fontWeight={600}>
                              Финансовая информация
                            </Heading>
                          </Flex>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <FinanceItem label="Макс ставка продажи (Общая)" value={currentApplication?.sales_max_offer_general} />
                            <FinanceItem label="Макс ставка продажи (МСК и МО)" value={currentApplication?.sales_max_offer_moscow} />
                            <FinanceItem label="Макс ставка продажи (Регион)" value={currentApplication?.sales_max_offer_regions} />
                            <FinanceItem label="Ставка закупки (max)" value={(currentApplication as any)?.purchase_rate_max} />
                            <FinanceItem
                              label="Маржинальность"
                              value={
                                typeof (currentApplication as any)?.marginality === "number"
                                  ? `${(currentApplication as any).marginality}%`
                                  : (currentApplication as any)?.marginality
                              }
                            />
                          </SimpleGrid>
                        </Box>

                        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                          <Flex alignItems="center" gap="8px" mb="16px">
                            <Heading as="h3" size="md" fontWeight={600}>
                              Условия работы
                            </Heading>
                          </Flex>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <InfoItem label="Формат работы">{currentApplication?.job_format?.name ?? "—"}</InfoItem>
                            <InfoItem label="Занятость">{currentApplication?.job_workload?.name ?? "—"}</InfoItem>
                            <InfoItem label="Локация поиска">{currentApplication?.location ?? "—"}</InfoItem>
                            <InfoItem label="Адрес офиса">{currentApplication?.office_address ?? "—"}</InfoItem>
                          </SimpleGrid>
                        </Box>

                        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                          <Flex alignItems="center" gap="8px" mb="16px">
                            <Heading as="h3" size="md" fontWeight={600}>
                              Дополнительная информация
                            </Heading>
                          </Flex>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <InfoItem label="Комментарии к заявке">{currentApplication?.comments || "—"}</InfoItem>
                            <InfoItem label="Вложения">
                              {(() => {
                                const raw = (currentApplication as any)?.attachments;
                                const list = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.values(raw) : [];
                                if (!list || list.length === 0) return "—";
                                return (
                                  <Flex gap="4px" flexWrap="wrap">
                                    {list.map((file: any) => (
                                      <Flex
                                        key={file.id || file.name}
                                        alignItems="center"
                                        gap="6px"
                                        bgColor="#f1f5f9"
                                        p="4px 8px"
                                        borderRadius="4px"
                                        sx={{ "&:hover": { cursor: "pointer", opacity: 0.8 } }}
                                        onClick={() => {
                                          if (file?.id && file?.name) {
                                            downloadFile(Number(file.id), String(file.name));
                                          }
                                        }}
                                      >
                                        <AttachmentIcon style={{ verticalAlign: "text-top" }} />
                                        <Text as="span">{file?.name || file?.filename || "Файл"}</Text>
                                      </Flex>
                                    ))}
                                  </Flex>
                                );
                              })()}
                            </InfoItem>
                          </SimpleGrid>
                        </Box>
                      </Flex>
                      <Flex flexDirection="column" gap="16px" minW={0} w={{ base: "100%", md: "360px" }}>
                        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                          <Heading as="h3" size="md" fontWeight={600} mb="16px">
                            Основная информация
                          </Heading>
                          <Flex flexDirection="column" gap="12px">
                            <InfoItem label="Название должности">{currentApplication?.name ?? "—"}</InfoItem>
                            <InfoItem label="Профиль">{(currentApplication as any)?.profile?.name ?? "—"}</InfoItem>
                            <InfoItem label="Грейд">
                              {Array.isArray(currentApplication?.candidate_grade) &&
                              currentApplication?.candidate_grade?.length &&
                              currentApplication?.candidate_grade?.length > 0
                                ? currentApplication?.candidate_grade?.map((g: any) => g?.name).join(", ")
                                : "—"}
                            </InfoItem>
                            <InfoItem label="Срок привлечения">{(currentApplication as any)?.attraction_period?.name ?? "—"}</InfoItem>
                            <InfoItem label="Требуется человек">{currentApplication?.required_number ?? "—"}</InfoItem>
                            <InfoItem label="Статус заявки">{(currentApplication as any)?.status?.name ?? "—"}</InfoItem>
                          </Flex>
                        </Box>
                        <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                          <Heading as="h3" size="md" fontWeight={600} mb="16px">
                            Информация о создании
                          </Heading>
                          <Flex flexDirection="column" gap="12px">
                            <InfoItem label="Кем создано">
                              {(currentApplication as any)?.created_by?.name ?? (currentApplication as any)?.created_by?.username ?? "—"}
                            </InfoItem>
                            <InfoItem label="Дата создания">
                              {currentApplication?.created_at ? formatDate(currentApplication.created_at) : "—"}
                            </InfoItem>
                          </Flex>
                        </Box>
                      </Flex>
                    </Flex>
                  </TabPanel>
                  <TabPanel paddingX={0}>
                    <Box border="1px solid #E5E7EB" borderRadius="12px" bg="white" p="20px">
                      <Heading as="h3" size="md" fontWeight={600} mb="8px">
                        Связанная вакансия
                      </Heading>
                      {currentApplication?.vacancy ? (
                        <Box>
                          <Flex direction="column" gap="16px">
                            <Box p="16px" bg="gray.50" borderRadius="8px">
                              <Flex alignItems="center" gap="8px" mb="12px">
                                <Icon as={FiShoppingBag as any} boxSize={5} color="#374151" />
                                <Text fontSize="18px" fontWeight={600} color="#111827">
                                  {currentApplication.vacancy.name}
                                </Text>
                              </Flex>
                              <Flex mt="16px" gap="8px">
                                <ChakraLink href={`/vacancy/${currentApplication.vacancy.id}`} _hover={{ textDecoration: "none" }}>
                                  <Button size="sm" colorScheme="blue" variant="outline" as="span">
                                    Перейти к вакансии
                                  </Button>
                                </ChakraLink>
                              </Flex>
                            </Box>
                          </Flex>
                        </Box>
                      ) : (
                        <Flex direction="column" alignItems="center" justifyContent="center" py="48px" gap="8px">
                          <Icon as={FiShoppingBag as any} boxSize={10} color="#9CA3AF" />
                          <Text fontSize="16px" color="#374151" fontWeight={600}>
                            Нет связанной вакансии
                          </Text>
                          <Text fontSize="14px" color="#6B7280">
                            К этой заявке пока не привязана активная вакансия
                          </Text>
                        </Flex>
                      )}
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Flex>
          </Flex>
        </Flex>
      </PageLoaderWrapper>
    </Layout>
  );
};

// История изменений в модальном окне
// Размещаем вне основного JSX для читаемости

export const HistoryModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => (
  <Modal isCentered isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
    <ModalOverlay backdropFilter="blur(3px)" />
    <ModalContent maxW="60vw" px="24px" paddingTop="32px" paddingBottom="24px">
      <ModalHeader p={0}>История изменений</ModalHeader>
      <ModalCloseButton />
      <ModalBody p={0} pt="8px" maxH="70vh" overflowY="auto">
        <HistoryApplicationBlock />
      </ModalBody>
    </ModalContent>
  </Modal>
);

const InfoItem = ({ label, children }: { label: string; children: any }) => {
  return (
    <Flex flexDirection="column" gap="6px" minW={0}>
      <Flex alignItems="center" gap="8px">
        <Text fontSize="14px" color="#374151" fontWeight={700}>
          {label}
        </Text>
      </Flex>
      <Text fontSize="16px" color="#111827" wordBreak="break-word" whiteSpace="pre-line">
        {children}
      </Text>
    </Flex>
  );
};

const FinanceItem = ({ label, value }: { label: string; value?: string | number }) => {
  const display = value === undefined || value === null || value === "" ? "—" : String(value);
  return (
    <Flex flexDirection="column" gap="6px" minW={0}>
      <Text fontSize="14px" color="#374151" fontWeight={700}>
        {label}
      </Text>
      <Text fontSize="16px" color="#111827" wordBreak="break-word">
        {display}
      </Text>
    </Flex>
  );
};
